import { useEffect, useState } from "react";
import { ExternalLink, Store, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { ESHOP_DODAVATELIA, jeHttpUrl, ukazkaUrl } from "@/lib/eshopSablony";
import {
  useNastaveniaGlobal,
  useUpdateNastaveniaGlobal,
} from "@/api/hooks/useNastaveniaGlobal";

/**
 * Adresy eshopov dodávateľov.
 *
 * Vypĺňa ich user, nie sú to konštanty v kóde: adresy sa menia a odkaz,
 * ktorý nikam nevedie, je horší než žiadny — preto sa tlačidlo
 * „vyhľadať" v Objednávkach bez vyplnenej šablóny vôbec nezobrazí.
 * Adresy, ktoré poznáme, sa dajú doplniť jedným klikom.
 *
 * **Do košíka to nič nevloží.** Na to by každý eshop potreboval API
 * a prihlásenie; toto otvorí výsledok vyhľadávania. Na rýchle
 * objednanie celého dodávateľa slúži „Kódy" v Objednávkach — väčšina
 * B2B eshopov vie hromadné pridanie vložením zoznamu kódov.
 */
export function EshopSablonySection(): JSX.Element {
  const query = useNastaveniaGlobal();
  const update = useUpdateNastaveniaGlobal();
  const [hodnoty, setHodnoty] = useState<Record<string, string>>({});

  const ulozene = query.data?.data?.eshop_sablony;
  // Adresy, ktoré appka pozná, chodia z backendu — zoznam v TS by sa pri
  // oprave rozišiel s tým, čo sa reálne použije na odkaz
  const predvolene = query.data?.data?.eshop_sablony_default ?? {};
  useEffect(() => {
    setHodnoty((ulozene ?? {}) as Record<string, string>);
  }, [ulozene]);

  async function uloz(): Promise<void> {
    try {
      await update.mutateAsync({ eshop_sablony: hodnoty });
      toast({ title: "Adresy eshopov uložené" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Neuložené",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <Store className="h-5 w-5 text-primary" />
          Vyhľadávanie v eshopoch
        </h2>
        <p className="text-xs text-muted-foreground">
          Adresa vyhľadávania dodávateľa so zástupným{" "}
          <code className="font-mono">{"{kod}"}</code> — v Objednávkach sa pri
          položke objaví odkaz, ktorý ju v eshope rovno nájde. Adresy, ktoré
          appka pozná, už fungujú; sem sa píše, len keď sa niektorá zmenila.
        </p>
      </div>

      <div className="space-y-3">
        {ESHOP_DODAVATELIA.map((d) => {
          const hodnota = hodnoty[d.kluc] ?? "";
          const platna = jeHttpUrl(hodnota);
          const predvolena = predvolene[d.kluc];
          return (
            <div
              key={d.kluc}
              className="grid grid-cols-1 gap-1 md:grid-cols-[160px_1fr] md:gap-x-2"
            >
              <Label
                htmlFor={`eshop-${d.kluc}`}
                className="self-center text-sm md:justify-self-start"
              >
                {d.label}
              </Label>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Input
                    id={`eshop-${d.kluc}`}
                    value={hodnota}
                    onChange={(e) =>
                      setHodnoty((h) => ({ ...h, [d.kluc]: e.target.value }))
                    }
                    placeholder={predvolena ?? "https://eshop.sk/hladat?q={kod}"}
                    className="h-9 font-mono text-xs"
                  />
                  {predvolena && hodnota.trim() === "" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="shrink-0"
                      onClick={() =>
                        setHodnoty((h) => ({ ...h, [d.kluc]: predvolena }))
                      }
                      title={`Doplniť známu adresu: ${predvolena}`}
                      // Tlačidiel je viac ako jedno a všetky sa volajú
                      // rovnako — bez mena dodávateľa čítačka nepovie, ktoré
                      aria-label={`Predvyplniť adresu ${d.label}`}
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      Predvyplniť
                    </Button>
                  ) : null}
                  {/* Skúšobný odkaz beží na NEULOŽENEJ hodnote z inputu —
                      backend validáciu ešte nevidel, takže schému musíme
                      overiť tu, inak by `javascript:` bolo klikateľné */}
                  {platna ? (
                    <a
                      href={ukazkaUrl(hodnota)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-primary hover:underline"
                      title="Vyskúšať adresu (vzorový kód F000814)"
                      aria-label={`Vyskúšať adresu ${d.label}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
                {hodnota.trim() !== "" && !platna ? (
                  <p className="text-[11px] text-destructive">
                    Musí začínať https:// — inak sa adresa neuloží.
                  </p>
                ) : (
                  <>
                    {/* Prázdne pole neznamená „bez odkazu" — bez tejto vety
                        by user nevedel, prečo odkaz funguje */}
                    {predvolena && hodnota.trim() === "" ? (
                      <p className="text-[11px] text-muted-foreground">
                        Používa sa predvolená adresa — vypĺňaj, len ak sa
                        zmenila.
                      </p>
                    ) : null}
                    {d.pomoc ? (
                      <p className="text-[11px] text-muted-foreground">
                        {d.pomoc}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground">
        <code className="font-mono">{"{kod}"}</code> je voliteľný — kde sa
        neobjednáva vyhľadaním (Häfele), stačí odkaz na správnu stránku a kód
        sa k nej skopíruje tlačidlom „Kódy". Vloženie do košíka appka spraviť
        nevie — na to má eshop vlastné prihlásenie.
      </p>

      <Button size="sm" disabled={update.isPending} onClick={() => void uloz()}>
        {update.isPending ? "Ukladám…" : "Uložiť"}
      </Button>
    </section>
  );
}
